import { useGetInventoryEnvironmentOptions } from "@/api/useInventory"
import { SEVERITIES_FILTER_OPTIONS } from "@/const/options.constants"
import { TAB_KEYS_BY_PAGE } from "@/const/tab.constants"
import { TEASERS } from "@/const/teaser.constants"
import { isDataAvailable } from "@/helpers/context.helper"
import { useInitialRender } from "@/hooks/useInitialRender"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useOrderBy } from "@/hooks/useOrderBy"
import { useTooltip } from "@/hooks/useTooltip"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { ScanProgressEmptyTable } from "@/molecules/EmptyTable"
import { ExportButton } from "@/molecules/ExportButton"
import { CODE_REPOSITORY_DRAWER_ID } from "@/organisms/Drawers/const"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { Filter } from "@/organisms/FilterNew/Filter"
import { InputSmartSearchLoading, SmartSearch } from "@/organisms/SmartSearch"
import {
  useBranch,
  useBranchArchivedRepos
} from "@/pages/DiscoveryPage/application/branch-inventory/useBranchInventory"
import { getBranchTabData } from "@/pages/DiscoveryPage/application/branch-inventory/useBranchInventory.selector"
import { AsideFooter } from "@/pages/DiscoveryPage/components/molecules/AsideFooter"
import { TabTemplate } from "@/templates/TabTemplate"
import { useEffect, useState } from "react"
import { getColumnsTable } from "./getColumnsTable"
import { getItemsFilter } from "./getItemsFilter"

import type { BranchTabProps } from "."

type TRecord = ReturnType<typeof getBranchTabData>["data"][number]

export const BranchTab: React.FC<BranchTabProps> = (props) => {
  const { useActiveKeyTabsInventory } = props
  const { activeKey, config = {} } = useActiveKeyTabsInventory()
  const { t } = useTranslation()
  const { t: tAsideFooter } = useTranslation("translation", {
    keyPrefix: "inventory.codeRepositoriesTab.asideFooter"
  })
  const { idsInDrawers, onRowClick } = useDrawer(CODE_REPOSITORY_DRAWER_ID, {
    filters: ["severity", "riskId", "runtime"]
  })

  const { environmentOptions, isLoading: isLoadingEnvironments } =
    useGetInventoryEnvironmentOptions()

  const filters = getItemsFilter(t, environmentOptions)
  const TAB_KEYS = TAB_KEYS_BY_PAGE.INVENTORY
  const context = useInitialRender(
    useBranch(filters, {
      enabled: activeKey === TAB_KEYS[0],
      ...config
    })
  )
  const { response, isLoading } = context
  const { response: archivedRepoResponse, isLoading: isLoadingArchived } =
    useBranchArchivedRepos()
  const { data, metadata } = response || {}
  const featureFlags = useFeatureFlags()
  const filterDisabled = featureFlags.dataReadiness || isLoadingEnvironments
  const previewMode = !!config.headers
  const [showArchivedData, setShowArchivedData] = useState(false)

  useEffect(() => {
    const showFooter: boolean = !isLoading && !isLoadingArchived

    if (!showArchivedData && showFooter) setShowArchivedData(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isLoadingArchived])

  const convertors = useNumberConvertor()
  const { onSorterChange, ...orderByProps } = useOrderBy()
  const noData = !isDataAvailable(context)
  const disableExport = noData || filterDisabled
  const placement = "code-repos-page"

  useTooltip(data)

  const showScanningProgress =
    noData && featureFlags.scmInstalled && !previewMode

  return (
    <TabTemplate<TRecord>
      resizable
      dataSource={data}
      metadata={metadata}
      columns={getColumnsTable({
        sorter: orderByProps,
        convertors,
        t,
        previewMode,
        onRowClick
      })}
      loading={isLoading}
      locale={{
        customEmptyComponent: showScanningProgress && (
          <ScanProgressEmptyTable isLoading={isLoading} />
        ),
        teasers: {
          dataReadiness: true,
          scmInstalled: TEASERS.SCM_INSTALLED.codeRepoNotConnected(t)
        }
      }}
      SearchComponent={
        !previewMode && (
          <Filter.Provider keys={filters}>
            <Filter.Template
              isLoading={filterDisabled}
              search={
                filterDisabled ? (
                  <InputSmartSearchLoading />
                ) : (
                  <SmartSearch
                    id="smart-search-code-repo-tab-inventory"
                    filters={filters}
                    searchedVulnerabilities={metadata?.searchedVulnerabilities}
                  />
                )
              }
              aside={
                <ExportButton isLoading={disableExport} exportType="Branch" />
              }
            >
              <Filter.Insights
                isLoading={filterDisabled}
                filters={filters}
                placement={placement}
              />
              <Filter.Checkbox
                isLoading={filterDisabled}
                order="none"
                keyFilter="severity"
                items={SEVERITIES_FILTER_OPTIONS}
                placeHolderDefaultValue="Severity"
                placement={placement}
              />
            </Filter.Template>
          </Filter.Provider>
        )
      }
      idsInDrawers={idsInDrawers}
      onRowClick={onRowClick}
      disableDrawer={previewMode}
      asideFooter={
        showArchivedData &&
        !!archivedRepoResponse?.data?.archived && (
          <AsideFooter
            label={`${archivedRepoResponse?.data.archived} ${tAsideFooter("content")}`}
            tooltip={tAsideFooter("popUpContent")}
          />
        )
      }
      onChange={(_pagination, _filters, sorter) => {
        onSorterChange(sorter)
      }}
    />
  )
}
